import { parseISO } from "date-fns";
import { ArrowLeft, Braces, ChevronRight, Database, ExternalLink, FileText, List, Waypoints } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { CopyButton } from "~/components/copy-button";
import { JsonTable } from "~/components/json-table";
import { SectionCard } from "~/components/section-card";
import { Badge, Button } from "~/components/ui";
import type { CompoundDetailUI, CompoundSourceRecordUI } from "~/features/compoundEntities/types";
import { cn } from "~/lib/utils";
import { v2Route } from "~/v2/routes";

interface CompoundEntityDetailPresenterProps {
  detail: CompoundDetailUI;
  sourceRecords: CompoundSourceRecordUI[];
  hasMoreSourceRecords: boolean;
  onLoadMoreSourceRecords: () => void;
  sourceRecordsLoading: boolean;
  sourceRecordsLoadFailed: boolean;
  onRetrySourceRecords: () => void;
  onBack: () => void;
  /** "page" = full-page route (own width + back button); "peek" = inside the Sheet. */
  variant?: "page" | "peek";
}

const isNonEmptyObject = (value: unknown): boolean =>
  value != null && (typeof value !== "object" || Object.keys(value as object).length > 0);

export function CompoundEntityDetailPresenter({
  detail,
  sourceRecords,
  hasMoreSourceRecords,
  onLoadMoreSourceRecords,
  sourceRecordsLoading,
  sourceRecordsLoadFailed,
  onRetrySourceRecords,
  onBack,
  variant = "page",
}: CompoundEntityDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";

  const hasRecordRelationship = detail.recordIdArray.length > 0 || Boolean(detail.sourceId) || Boolean(detail.targetId);
  const hasReferences = detail.referenceArray.length > 0;
  const hasMetadata = isNonEmptyObject(detail.metadata);
  const isEntityCompound = detail.recordType === "entity";

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="min-w-0 space-y-1">
        {!isPeek && (
          <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
            <ArrowLeft />
            <span>{t("ui.back")}</span>
          </Button>
        )}
        <div className="flex items-center gap-2">
          <h1 className={cn("text-fg-1", isPeek ? "text-16-bold" : "text-dewey-title")}>
            {detail.displayName || detail.id}
          </h1>
          {detail.recordType && <Badge tone="default">{detail.recordType}</Badge>}
        </div>
        <div className="flex items-center gap-1.5 text-12-regular text-fg-3">
          <span className="tabular-nums">{detail.id}</span>
          <CopyButton value={detail.id} label={t("ui.copyId")} successMessageKey="feature.common.idCopied" />
        </div>
      </div>

      <SectionCard
        icon={List}
        title={t("feature.compoundEntities.detail.representativeValues")}
        contentClassName="overflow-x-auto p-0"
      >
        <table className="w-full border-collapse text-[13px]">
          <tbody>
            <InfoRow label={t("model.common.name")} value={detail.name} />
            <InfoRow label={t("model.common.nameJa")} value={detail.nameJa} />
            <InfoRow label={t("model.geoItem.startDate")} value={detail.date} />
            <InfoRow label={t("model.geoItem.endDate")} value={detail.endDate} />
            <InfoRow
              label={t("feature.compoundEntities.detail.categoryNames")}
              value={detail.categoryNameArray.join(", ")}
            />
          </tbody>
        </table>
      </SectionCard>

      {hasRecordRelationship && (
        <SectionCard
          icon={Waypoints}
          title={
            isEntityCompound
              ? t("feature.compoundEntities.detail.sourceEntities")
              : t("feature.compoundEntities.detail.recordRelationship")
          }
          action={isEntityCompound ? <Badge tone="default">{detail.recordIdArray.length}</Badge> : undefined}
        >
          <div className="space-y-3">
            {detail.recordIdArray.length > 0 &&
              (isEntityCompound ? (
                <SourceEntityList
                  records={sourceRecords}
                  hasMore={hasMoreSourceRecords}
                  onLoadMore={onLoadMoreSourceRecords}
                  loading={sourceRecordsLoading}
                  loadFailed={sourceRecordsLoadFailed}
                  onRetry={onRetrySourceRecords}
                />
              ) : (
                <ChipRow label={t("feature.compoundEntities.detail.recordIds")} items={detail.recordIdArray} mono />
              ))}
            {detail.sourceId && <KeyValue label={t("model.record.relation.source")} value={detail.sourceId} mono />}
            {detail.targetId && <KeyValue label={t("model.record.relation.target")} value={detail.targetId} mono />}
          </div>
        </SectionCard>
      )}

      {hasReferences && (
        <SectionCard icon={FileText} title={t("model.reference.modelNames")} contentClassName="space-y-3">
          {detail.referenceArray.map((ref, i) => (
            <ReferenceItem key={i} reference={ref} />
          ))}
        </SectionCard>
      )}

      <SectionCard icon={Braces} title={t("model.changeRequest.properties")}>
        <JsonTable target={detail.properties} />
      </SectionCard>

      <SectionCard icon={Database} title={t("feature.compoundEntities.detail.technicalInfo")}>
        <div className="overflow-x-auto rounded-[var(--radius-xs)] border border-border-default">
          <table className="w-full border-collapse text-[13px]">
            <tbody>
              <InfoRow label={t("model.common.key")} value={detail.key} mono />
              <InfoRow label={t("ui.version")} value={detail.version} />
              <InfoRow
                label={t("feature.compoundEntities.detail.categoryIds")}
                value={detail.categoryIdArray.join(", ")}
                mono
              />
              <InfoRow
                label={t("model.common.createdAt")}
                value={detail.createdAt ? t("date.long", { date: parseISO(detail.createdAt) }) : ""}
              />
              <InfoRow
                label={t("model.common.updatedAt")}
                value={detail.updatedAt ? t("date.long", { date: parseISO(detail.updatedAt) }) : ""}
              />
            </tbody>
          </table>
        </div>
        {hasMetadata && (
          <div className="space-y-2">
            <div className="text-12-bold capitalize text-fg-2">{t("model.common.metadata")}</div>
            <JsonTable target={detail.metadata} />
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function SourceEntityList({
  records,
  hasMore,
  onLoadMore,
  loading,
  loadFailed,
  onRetry,
}: {
  records: CompoundSourceRecordUI[];
  hasMore: boolean;
  onLoadMore: () => void;
  loading: boolean;
  loadFailed: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (loading && records.length === 0) {
    return <div className="py-2 text-[13px] text-fg-3">{t("ui.loading")}</div>;
  }

  if (loadFailed && records.length === 0) {
    return <SourceEntityLoadError onRetry={onRetry} />;
  }

  return (
    <div className="space-y-2">
      {records.map((record) => (
        <details
          key={record.id}
          className="group overflow-hidden rounded-[var(--radius-md)] border border-border-default"
        >
          <summary className="grid cursor-pointer list-none grid-cols-[minmax(0,1fr)_auto] items-center gap-3 bg-bg-subtle px-3.5 py-3 hover:bg-primary-50 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-4 [&::-webkit-details-marker]:hidden">
            <span className="min-w-0 truncate text-[13px] font-bold text-fg-1">{record.displayName}</span>
            <span className="order-3 col-span-2 grid grid-cols-2 gap-3 sm:order-2 sm:col-span-1 sm:gap-4">
              <DateValue label={t("model.geoItem.startDate")} value={record.date} />
              <DateValue label={t("model.geoItem.endDate")} value={record.endDate} />
            </span>
            <ChevronRight className="order-2 h-4 w-4 text-fg-3 transition-transform group-open:rotate-90 sm:order-3" />
          </summary>
          <div className="space-y-4 border-t border-border-default px-3.5 py-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <KeyValue label={t("model.common.name")} value={record.name} />
              <KeyValue label={t("model.common.nameJa")} value={record.nameJa} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px] text-fg-3">
                <span className="break-all tabular-nums">{record.id}</span>
                <CopyButton value={record.id} />
              </span>
              <Button type="button" variant="outline" size="sm" asChild>
                <Link to={v2Route.entities.$buildPath({ searchParams: { selectedEntity: record.id } })} target="_blank">
                  <ExternalLink />
                  <span>{t("ui.openInFullPage")}</span>
                </Link>
              </Button>
            </div>
            {record.categoryNames.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {record.categoryNames.map((category) => (
                  <Badge key={category} tone="default">
                    {category}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </details>
      ))}
      {loadFailed && <SourceEntityLoadError onRetry={onRetry} />}
      {hasMore && (
        <Button type="button" variant="outline" className="w-full" onClick={onLoadMore} disabled={loading}>
          {t(loading ? "ui.loading" : "feature.compoundEntities.detail.loadMoreSourceEntities")}
        </Button>
      )}
    </div>
  );
}

function SourceEntityLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-xs)] border border-danger px-3.5 py-3"
    >
      <span className="text-[13px] text-danger">{t("feature.compoundEntities.detail.sourceEntitiesLoadFailed")}</span>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        {t("feature.compoundEntities.detail.retrySourceEntities")}
      </Button>
    </div>
  );
}

function DateValue({ label, value }: { label: string; value: string }) {
  return (
    <span className="grid min-w-0 gap-0.5 text-left sm:min-w-24">
      <span className="text-[10px] font-medium text-fg-3">{label}</span>
      <span className="whitespace-nowrap text-[12px] tabular-nums text-fg-1">{value || "—"}</span>
    </span>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <tr className="border-b border-border-default align-top last:border-b-0">
      <th className="w-[8rem] whitespace-nowrap border-r border-border-default bg-bg-subtle px-3.5 py-2.5 text-left align-top font-bold text-fg-1 sm:w-[12rem]">
        {label}
      </th>
      <td className={cn("px-3.5 py-2.5 text-fg-2", mono ? "break-all text-[12px]" : "whitespace-pre-wrap break-words")}>
        {value || "—"}
      </td>
    </tr>
  );
}

function KeyValue({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-12-bold capitalize text-fg-2">{label}</span>
      <span className="inline-flex items-center gap-1.5">
        <span className={cn("text-fg-1", mono && "break-all text-[12px]")}>{value}</span>
        <CopyButton value={value} />
      </span>
    </div>
  );
}

function ChipRow({ label, items, mono }: { label: string; items: string[]; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-12-bold capitalize text-fg-2">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge
            key={`${item}:${i}`}
            tone="default"
            className={cn("inline-flex items-center gap-1", mono && "font-normal")}
          >
            <span className={cn(mono && "break-all")}>{item}</span>
            <CopyButton value={item} className="h-4 w-4" />
          </Badge>
        ))}
      </div>
    </div>
  );
}

function ReferenceItem({ reference }: { reference: unknown }) {
  const { t } = useTranslation();
  const obj = (reference ?? {}) as Record<string, unknown>;
  const refId = typeof obj.id === "string" ? obj.id : undefined;

  return (
    <div className="space-y-2 rounded-[var(--radius-xs)] border border-border-default p-3">
      {refId && (
        <div className="flex items-center gap-1.5 text-[12px]">
          <span className="text-12-bold capitalize text-fg-2">{t("model.common.id")}</span>
          <span className="break-all tabular-nums text-fg-1">{refId}</span>
          <CopyButton value={refId} label={t("ui.copyId")} successMessageKey="feature.common.idCopied" />
        </div>
      )}
      <JsonTable target={reference} />
    </div>
  );
}
